import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Save, Loader2, Check, Tags } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keep in sync with onboarding's PROVIDER_CATEGORIES values.
const PROVIDER_TYPES = [
  { value: 'FARRIER', label: 'Farrier' },
  { value: 'RIDING_INSTRUCTOR', label: 'Riding Instructor' },
  { value: 'TRAINER', label: 'Horse Trainer' },
  { value: 'HAULING', label: 'Horse Hauling' },
  { value: 'RANCH_SERVICE', label: 'Ranch Service' },
  { value: 'FENCE_REPAIR', label: 'Fence Repair' },
  { value: 'EQUIPMENT_WORK', label: 'Equipment Work' },
  { value: 'VET', label: 'Mobile Vet' },
  { value: 'GUIDED_TRAIL_RIDES', label: 'Guided Trail Rides' },
  { value: 'RIDING_STABLE', label: 'Riding Stable' },
  { value: 'STABLE_SERVICES', label: 'Stable Services' },
  { value: 'OTHER', label: 'Other' },
];

// RW-FIX-003: edit the provider's selected types (multi-select). Self-contained —
// loads from and saves to /api/dashboard/profile without touching other settings.
export default function ProviderTypesCard() {
  const { token } = useAuth();
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/dashboard/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!active) return;
        const d = res.data || {};
        const cats = (d.providerCategories && d.providerCategories.length > 0)
          ? d.providerCategories
          : (d.providerCategory ? [d.providerCategory] : []);
        setSelected(cats);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  const toggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one provider type');
      return;
    }
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/api/dashboard/profile`, { providerCategories: selected }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Provider types updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update provider types');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="provider-types-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tags className="h-5 w-5 text-primary" /> Provider Types
        </CardTitle>
        <CardDescription>
          Choose every type of service you offer. You'll appear in search results under each one. (This is separate from your services.)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="provider-types-options">
              {PROVIDER_TYPES.map((t) => {
                const on = selected.includes(t.value);
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => toggle(t.value)}
                    data-testid={`provider-type-${t.value}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all ${
                      on
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-input hover:border-primary/50 text-foreground'
                    }`}
                  >
                    {on && <Check className="h-3.5 w-3.5" />}
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between">
              <Badge variant="secondary" data-testid="provider-types-count">
                {selected.length} selected
              </Badge>
              <Button onClick={save} disabled={saving || selected.length === 0} data-testid="provider-types-save">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Types
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
